import { Button, DialogContent, DialogTitle, Spinner } from '@atlas/ui-primitives'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { MappingPreviewDTO } from '@/services/workfront-mappings.service'

/** A before or after value as one line: lists joined, objects as JSON, nothing as a dash. */
export function shownValue(value: unknown): string {
	if (value === null || value === undefined || value === '') return '—'
	if (Array.isArray(value)) return value.length ? value.map(shownValue).join(', ') : '—'
	if (typeof value === 'object') return JSON.stringify(value)
	return String(value)
}

const count = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`

/** The preview's three numbers, shown by the preview and again before a publish. */
export function PreviewTotals({ preview }: { preview: MappingPreviewDTO }) {
	return (
		<ul aria-label="Preview totals" className="grid gap-2 text-ds-sm sm:grid-cols-3">
			<li className="rounded-ds-sm bg-ds-surface p-3">
				<span className="block font-semibold text-ds-lg">{preview.changedWorkOrders.toLocaleString('en-US')}</span>
				{preview.changedWorkOrders === 1 ? 'work order would change' : 'work orders would change'}, of{' '}
				{count(preview.bundles, 'bundle', 'bundles')}
			</li>
			<li className="rounded-ds-sm bg-ds-surface p-3">
				<span className="block font-semibold text-ds-lg">{preview.released.length}</span>
				{preview.released.length === 1 ? 'hold released' : 'holds released'}
			</li>
			<li className="rounded-ds-sm bg-ds-surface p-3">
				<span className="block font-semibold text-ds-lg">{preview.newErrors.length}</span>
				{preview.newErrors.length === 1 ? 'new error' : 'new errors'}
			</li>
		</ul>
	)
}

const bundleHref = (bundleId: string) => `/admin/workfront/bundles/${bundleId}`

/** One target's change, its examples one press away. */
function TargetChange({ change }: { change: MappingPreviewDTO['byTarget'][number] }) {
	const [open, setOpen] = useState(false)
	return (
		<li className="border-ds-border border-b py-2">
			<div className="flex items-center justify-between gap-2 text-ds-sm">
				<span>
					<code>{change.path}</code>: {count(change.count, 'change', 'changes')}
				</span>
				{change.examples.length ? (
					<Button size="sm" variant="ghost" aria-expanded={open} onPress={() => setOpen(!open)}>
						{open
							? 'Hide examples'
							: `Show ${change.examples.length} example${change.examples.length === 1 ? '' : 's'}`}
					</Button>
				) : null}
			</div>
			{open ? (
				<ul className="mt-2 space-y-1 text-ds-xs">
					{change.examples.map((example) => (
						<li key={`${example.bundleId}:${example.workfrontId}`}>
							<Link className="font-medium hover:underline" to={bundleHref(example.bundleId)}>
								{example.title ?? example.workfrontId}
							</Link>
							: {shownValue(example.before)} → {shownValue(example.after)}
						</li>
					))}
				</ul>
			) : null}
		</li>
	)
}

/** Who the PM rules would give the work orders with no PM today, per PM. */
export function PmAssignments({ preview, nameOf }: { preview: MappingPreviewDTO; nameOf: (userId: string) => string }) {
	const total = preview.pmAssignments.reduce((sum, assignment) => sum + assignment.workOrders, 0)
	return (
		<section>
			<h3 className="font-medium">PM assignments</h3>
			<p className="text-ds-sm">{count(total, 'work order with no PM gets one', 'work orders with no PM get one')}</p>
			<ul className="text-ds-sm">
				{preview.pmAssignments.map((assignment) => (
					<li key={assignment.pmUserId}>
						{nameOf(assignment.pmUserId)}:{' '}
						<span className="text-ds-text-muted">{count(assignment.workOrders, 'work order', 'work orders')}</span>
					</li>
				))}
			</ul>
		</section>
	)
}

/** The preview's body: totals, the change per target, the PMs assigned, the holds released and the new errors. */
export function PreviewBody({
	preview,
	nameOf = (userId) => userId,
}: {
	preview: MappingPreviewDTO
	nameOf?: (userId: string) => string
}) {
	return (
		<div className="space-y-4">
			<PreviewTotals preview={preview} />
			<section>
				<h3 className="font-medium">Change per target</h3>
				{preview.byTarget.length ? (
					<ul>
						{preview.byTarget.map((change) => (
							<TargetChange key={change.path} change={change} />
						))}
					</ul>
				) : (
					<p className="text-ds-sm text-ds-text-muted">No mapped value would change.</p>
				)}
			</section>
			{preview.pmAssignments.length ? <PmAssignments preview={preview} nameOf={nameOf} /> : null}
			{preview.released.length ? (
				<section>
					<h3 className="font-medium">Released from hold</h3>
					<ul className="text-ds-sm">
						{preview.released.map((hold) => (
							<li key={hold.workfrontId}>
								<Link className="hover:underline" to={bundleHref(hold.bundleId)}>
									{hold.role} {hold.workfrontId}
								</Link>
							</li>
						))}
					</ul>
				</section>
			) : null}
			{preview.newErrors.length ? (
				<section>
					<h3 className="font-medium">New errors</h3>
					<ul className="text-ds-sm">
						{preview.newErrors.map((error) => (
							<li key={`${error.code}:${error.title}`}>
								{error.title} <span className="text-ds-text-muted">({count(error.affected, 'object', 'objects')})</span>
							</li>
						))}
					</ul>
				</section>
			) : null}
		</div>
	)
}

export interface PreviewSummaryProps {
	open: boolean
	/** Null while the preview is computed. */
	preview: MappingPreviewDTO | null
	/** The preview could not be computed; the failure is reported in the status bar. */
	failed?: boolean
	onRetry?: () => void
	onClose: () => void
	onPublish: () => void
	/** A user's display name, for the PMs the draft would assign. */
	nameOf?: (userId: string) => string
}

/** What the draft would do to the stored bundles, without applying anything. */
export function PreviewSummary({
	open,
	preview,
	failed = false,
	onRetry,
	onClose,
	onPublish,
	nameOf,
}: PreviewSummaryProps) {
	if (!open) return null
	return (
		<DialogContent size="xl" isOpen={open} isDismissable onOpenChange={(next) => (next ? undefined : onClose())}>
			<DialogTitle>Preview of the draft</DialogTitle>
			<div className="mt-4 max-h-96 overflow-y-auto">
				{preview ? (
					<PreviewBody preview={preview} nameOf={nameOf} />
				) : failed ? (
					<div className="flex items-center gap-3">
						<p>The preview could not be computed.</p>
						{onRetry ? (
							<Button variant="outline" onPress={onRetry}>
								Retry
							</Button>
						) : null}
					</div>
				) : (
					<Spinner aria-label="Computing the preview" />
				)}
			</div>
			<div className="mt-6 flex justify-end gap-2">
				<Button variant="outline" onPress={onClose}>
					Close
				</Button>
				<Button isDisabled={!preview} onPress={onPublish}>
					Publish…
				</Button>
			</div>
		</DialogContent>
	)
}
